import { useUser, useClerk } from '@clerk/react';
import { CheckoutButton } from '@clerk/react/experimental';
import { useEffect } from 'react';

const PLAN_ID = process.env.REACT_APP_CLERK_PLAN_ID;

export default function Pricing() {
  const { isSignedIn } = useUser();
  const clerk = useClerk();

  // After sign-in/sign-up redirects back to /pricing, auto-open the pending checkout
  useEffect(() => {
    if (!isSignedIn) return;
    const pending = sessionStorage.getItem('pendingCheckoutPlan');
    if (!pending) return;
    sessionStorage.removeItem('pendingCheckoutPlan');
    setTimeout(() => {
      clerk.__internal_openCheckout({ planId: PLAN_ID, planPeriod: pending });
    }, 300);
  }, [isSignedIn, clerk]);

  const handleSubscribeClick = (plan) => {
    sessionStorage.setItem('pendingCheckoutPlan', plan);
    clerk.openSignUp({
      afterSignInUrl: '/pricing',
      afterSignUpUrl: '/pricing',
    });
  };
  const pageStyle = {
    maxWidth: "860px",
    margin: "40px auto",
    padding: "24px 20px 48px",
    color: "white",
    backgroundColor: "black",
    border: "1px solid white",
    borderRadius: "8px",
  };

  const cardBase = {
    flex: "1 1 220px",
    borderRadius: "10px",
    padding: "28px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  };

  const freeCard   = { ...cardBase, background: "#111", border: "1px solid #a09f9f" };
  const monthCard  = { ...cardBase, background: "#0d1a0d", border: "2px solid #28a745" };
  const lifeCard   = { ...cardBase, background: "#0d0d1a", border: "2px solid #ffd600" };

  const featureListStyle = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flexGrow: 1,
  };

  const check = (color = "#28a745") => (
    <span style={{ color, fontWeight: "bold", marginRight: "8px" }}>✓</span>
  );
  const cross = () => (
    <span style={{ color: "#555", fontWeight: "bold", marginRight: "8px" }}>✗</span>
  );
  const soon = () => (
    <span style={{ color: "#888", fontSize: "0.75rem", marginLeft: "6px" }}>(coming soon)</span>
  );

  const featureRow = (included, label, note) => (
    <li style={{ fontSize: "0.92rem", color: included ? "#ddd" : "#555", display: "flex", alignItems: "flex-start" }}>
      {included ? check() : cross()}
      <span>{label}{note && soon()}</span>
    </li>
  );

  const ctaBtn = (label, color, disabled = true, onClick = null) => (
    <button
      disabled={disabled}
        onClick={onClick}
        style={{
        marginTop: "auto",
        padding: "12px",
        borderRadius: "6px",
        border: "none",
        background: disabled ? "#333" : color,
        color: disabled ? "#666" : "#fff",
        fontWeight: "700",
        fontSize: "1rem",
        cursor: disabled ? "not-allowed" : "pointer",
        width: "100%",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={pageStyle}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "36px" }}>
        <h1 style={{ fontSize: "2.2rem", color: "#ffd600", marginBottom: "6px" }}>Pricing</h1>
        <p style={{ fontSize: "1.05rem", color: "#aaa", margin: 0 }}>
          Start free. Upgrade when you want more.
        </p>
      </div>

      {/* Cards */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "stretch", marginBottom: "40px" }}>

        {/* Free */}
        <div style={freeCard}>
          <div style={{ background: "#a09f9f", color: "#fff", fontSize: "0.75rem", fontWeight: "700", borderRadius: "4px", padding: "2px 8px", display: "block", marginBottom: "4px", textAlign: "center" }}>BASIC</div>
          <div>
            <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#fff", marginBottom: "4px" }}>Free</div>
            <div style={{ fontSize: "2rem", fontWeight: "800", color: "#fff" }}>$0</div>
            <div style={{ fontSize: "0.85rem", color: "#888" }}>No credit card required</div>
          </div>
          <ul style={featureListStyle}>
            {featureRow(true,  "Keno Analyzer")}
            {featureRow(true,  "Integrated Keno Optimizer")}
            {featureRow(true,  "Auto sessions up to 25 games")}
            {featureRow(true,  "1 saved configuration")}
            {featureRow(true,  "User-selectable Pay Tables")}
            {featureRow(false, "Unlimited session length")}
            {featureRow(false, "Unlimited saved configurations")}
            {featureRow(false, "Background sessions")}
            {featureRow(false, "Customizable Pay Tables")}
          </ul>
          {isSignedIn
            ? ctaBtn("Go to Keno Analyzer", "#007bff", false, () => (window.location.href = "/analyzer"))
            : ctaBtn("Join Free", "#28a745", false, () => (window.location.href = "/join"))}
        </div>

        {/* Monthly */}
        <div style={monthCard}>
          <div style={{ background: "#28a745", color: "#fff", fontSize: "0.75rem", fontWeight: "700", borderRadius: "4px", padding: "2px 8px", display: "block", marginBottom: "4px", textAlign: "center" }}>MOST POPULAR</div>
          <div>
            <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#fff", marginBottom: "4px" }}>Premium Monthly</div>
            <div style={{ fontSize: "2rem", fontWeight: "800", color: "#28a745" }}>$2.99<span style={{ fontSize: "1rem", color: "#aaa", fontWeight: "400" }}> / mo</span></div>
            <div style={{ fontSize: "0.85rem", color: "#888" }}>Cancel any time</div>
          </div>
          <ul style={featureListStyle}>
            {featureRow(true, "Everything in Free")}
            {featureRow(true, "Unlimited session length")}
            {featureRow(true, "Unlimited saved configurations")}
            {featureRow(true, "Background sessions")}
            {featureRow(true, "Customizable Pay Tables")}
          </ul>
          {isSignedIn
            ? <CheckoutButton planId={PLAN_ID} planPeriod="month">
                {ctaBtn("Subscribe Monthly", "#28a745", false)}
              </CheckoutButton>
            : ctaBtn("Subscribe Monthly", "#28a745", false, () => handleSubscribeClick("month"))}
        </div>

        {/* Lifetime */}
        <div style={lifeCard}>
          <div style={{ background: "#ffd600", color: "#000", fontSize: "0.75rem", fontWeight: "700", borderRadius: "4px", padding: "2px 8px", display: "block", marginBottom: "4px", textAlign: "center" }}>BEST VALUE</div>
          <div>
            <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "#fff", marginBottom: "4px" }}>Premium Annual</div>
            <div style={{ fontSize: "2rem", fontWeight: "800", color: "#ffd600" }}>$24.96<span style={{ fontSize: "1rem", color: "#aaa", fontWeight: "400" }}> yearly</span></div>
            <div style={{ fontSize: "0.85rem", color: "#888" }}>Over 30% Savings</div>
          </div>
          <ul style={featureListStyle}>
            {featureRow(true, "Everything in Premium Monthly")}
            {featureRow(true, "Significant savings over Monthly")}
            {featureRow(true, "All future premium features")}
          </ul>
          {isSignedIn
            ? <CheckoutButton planId={PLAN_ID} planPeriod="annual">
                {ctaBtn("Subscribe Annually", "#ffd600", false)}
              </CheckoutButton>
            : ctaBtn("Subscribe Annually", "#ffd600", false, () => handleSubscribeClick("annual"))}
        </div>
      </div>

      {/* Footer note */}
      <p style={{ textAlign: "center", fontSize: "0.88rem", color: "#555" }}>
        Payments processed securely via Stripe. Subscription accounts managed via Clerk.
      </p>
    </div>
  );
}
